import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Modal, Alert, Spinner, Badge } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSave } from 'react-icons/fi';
import { departmentAPI, userAPI } from '../../utils/api';
import '../styles/DepartmentManagement.css';

const DepartmentManagement = () => {
  const getApiErrorMessage = (err, fallback) => {
    if (Array.isArray(err?.errors) && err.errors.length > 0) {
      return err.errors.map((item) => item?.message).filter(Boolean).join(', ');
    }
    return err?.message || fallback;
  };

  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    headId: '',
    budget: 0,
    isActive: true
  });

  const [selectedDept, setSelectedDept] = useState(null);
  const [deptToDelete, setDeptToDelete] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Fetch departments and users
   */
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch departments
      const deptsResponse = await departmentAPI.getAllDepartments();
      if (deptsResponse.success && deptsResponse.data) {
        setDepartments(deptsResponse.data);
      }

      // Fetch users
      const usersResponse = await userAPI.getAllUsers();
      if (usersResponse.success && usersResponse.data) {
        setUsers(usersResponse.data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    if (!String(formData.name || '').trim()) {
      setError('Department name is required');
      return false;
    }

    return true;
  };

  /**
   * Handle create department
   */
  const handleCreateDept = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const payload = {
        name: String(formData.name || '').trim(),
        description: String(formData.description || '').trim(),
        headId: formData.headId || null,
        isActive: Boolean(formData.isActive)
      };
      const response = await departmentAPI.createDepartment(payload);
      if (response.success && response.data) {
        setSuccess('Department created successfully!');
        setDepartments([...departments, response.data]);
        setShowCreateModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Create error:', err);
      setError(getApiErrorMessage(err, 'Failed to create department'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle update department
   */
  const handleUpdateDept = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const payload = {
        name: String(formData.name || '').trim(),
        description: String(formData.description || '').trim(),
        headId: formData.headId || null,
        isActive: Boolean(formData.isActive)
      };
      const response = await departmentAPI.updateDepartment(selectedDept.id, payload);
      if (response.success && response.data) {
        setSuccess('Department updated successfully!');
        setDepartments(departments.map(d => d.id === selectedDept.id ? response.data : d));
        setShowEditModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(getApiErrorMessage(err, 'Failed to update department'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle delete department
   */
  const handleDeleteDept = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      await departmentAPI.deleteDepartment(deptToDelete.id);
      setSuccess('Department deleted successfully!');
      setDepartments(departments.filter(d => d.id !== deptToDelete.id));
      setShowDeleteModal(false);
      setDeptToDelete(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError(getApiErrorMessage(err, 'Failed to delete department'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle edit modal open
   */
  const handleOpenEditModal = (dept) => {
    setSelectedDept(dept);
    setFormData({
      name: dept.name || '',
      code: dept.code || '',
      description: dept.description || '',
      headId: dept.headId || '',
      budget: dept.budget || 0,
      isActive: dept.isActive !== false
    });
    setShowEditModal(true);
  };

  /**
   * Handle view modal open
   */
  const handleOpenViewModal = (dept) => {
    setSelectedDept(dept);
    setShowViewModal(true);
  };

  /**
   * Handle delete modal open
   */
  const handleOpenDeleteModal = (dept) => {
    setDeptToDelete(dept);
    setShowDeleteModal(true);
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      headId: '',
      budget: 0,
      isActive: true
    });
  };

  /**
   * Get head name
   */
  const getHeadName = (headId) => {
    if (!headId) return 'Not Assigned';
    const user = users.find(u => u.id === headId);
    return user ? `${user.firstName} ${user.lastName}` : 'Not Found';
  };

  /**
   * Get department users count
   */
  const getDeptUsersCount = (deptId) => {
    const departmentId = Number(deptId);
    return users.filter((user) => {
      const mappedDepartmentIds = Array.isArray(user.departmentIds)
        ? user.departmentIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [];
      if (mappedDepartmentIds.includes(departmentId)) return true;
      return Number(user.departmentId) === departmentId;
    }).length;
  };

  /**
   * Filter departments
   */
  const filteredDepartments = departments.filter((d) =>
    String(d?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(d?.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(d?.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading departments...</p>
      </div>
    );
  }

  return (
    <Container fluid className="dept-management">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <div className="management-header mb-4">
        <div>
          <h2>Manage Departments</h2>
          <p className="text-muted">Create and manage organizational departments</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => setShowCreateModal(true)}
          className="create-btn"
        >
          <FiPlus className="me-2" />
          Add Department
        </Button>
      </div>

      {/* Search */}
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Form.Control
            type="text"
            placeholder="Search departments by name, code, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input"
          />
        </Card.Body>
      </Card>

      {/* Departments Table */}
      {filteredDepartments.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5 text-muted">
            <p>No departments found</p>
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
            >
              <FiPlus className="me-2" />
              Create First Department
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <h5 className="mb-0">All Departments</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="dept-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Members</th>
                    <th>Head</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDepartments.map(dept => (
                    <tr key={dept.id} className="dept-row">
                      <td>
                        <strong>{dept.name}</strong>
                        <br />
                        <small className="text-muted">{dept.description}</small>
                      </td>
                      <td>
                        <Badge bg="light" text="dark">
                          {getDeptUsersCount(dept.id)}
                        </Badge>
                      </td>
                      <td>
                        <small>{getHeadName(dept.headId)}</small>
                      </td>
                      <td>
                        <Badge bg={dept.isActive ? 'success' : 'secondary'}>
                          {dept.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleOpenViewModal(dept)}
                          className="me-2"
                          title="View"
                        >
                          <FiEye size={14} />
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleOpenEditModal(dept)}
                          className="me-2"
                          title="Edit"
                        >
                          <FiEdit2 size={14} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleOpenDeleteModal(dept)}
                          title="Delete"
                        >
                          <FiTrash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiPlus className="me-2" />
            Create New Department
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleCreateDept}>
            <Row>
              {/* Name */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter department name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>

              {/* Code */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department Code</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., DEPT001"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter department description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            <Row>
              {/* Head */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department Head</Form.Label>
                  <Form.Select
                    value={formData.headId}
                    onChange={(e) => setFormData({ ...formData, headId: e.target.value ? parseInt(e.target.value) : '' })}
                    disabled={submitting}
                    className="form-input"
                  >
                    <option value="">Select Head</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Budget */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Budget ($)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Active */}
            <Form.Check
              type="checkbox"
              label="Department is Active"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              disabled={submitting}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreateModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateDept}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              <>
                <FiPlus className="me-2" />
                Create Department
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEdit2 className="me-2" />
            Edit Department
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleUpdateDept}>
            <Row>
              {/* Name */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter department name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>

              {/* Code */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department Code</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., DEPT001"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Description */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter department description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={submitting}
                className="form-input"
              />
            </Form.Group>

            <Row>
              {/* Head */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Department Head</Form.Label>
                  <Form.Select
                    value={formData.headId}
                    onChange={(e) => setFormData({ ...formData, headId: e.target.value ? parseInt(e.target.value) : '' })}
                    disabled={submitting}
                    className="form-input"
                  >
                    <option value="">Select Head</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Budget */}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Budget ($)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Enter budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                    disabled={submitting}
                    className="form-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Active */}
            <Form.Check
              type="checkbox"
              label="Department is Active"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              disabled={submitting}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              resetForm();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateDept}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <FiSave className="me-2" />
                Update Department
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>
            <FiEye className="me-2" />
            Department Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedDept && (
            <div className="dept-details">
              <div className="detail-group">
                <label>Department Name</label>
                <p>{selectedDept.name}</p>
              </div>

              <div className="detail-group">
                <label>Department Code</label>
                <p><code>{selectedDept.code || '-'}</code></p>
              </div>

              <div className="detail-group">
                <label>Description</label>
                <p>{selectedDept.description || 'No description'}</p>
              </div>

              <div className="detail-group">
                <label>Department Head</label>
                <p>{getHeadName(selectedDept.headId)}</p>
              </div>

              <div className="detail-group">
                <label>Total Members</label>
                <p>{getDeptUsersCount(selectedDept.id)}</p>
              </div>

              <div className="detail-group">
                <label>Budget</label>
                <p>${selectedDept.budget?.toLocaleString() || '0'}</p>
              </div>

              <div className="detail-group">
                <label>Status</label>
                <p>
                  <Badge bg={selectedDept.isActive ? 'success' : 'secondary'}>
                    {selectedDept.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </p>
              </div>

              <div className="detail-group">
                <label>Created Date</label>
                <p>{new Date(selectedDept.createdAt).toLocaleString()}</p>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowViewModal(false);
              handleOpenEditModal(selectedDept);
            }}
          >
            <FiEdit2 className="me-2" />
            Edit
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {deptToDelete && (
            <div className="delete-confirmation">
              <div className="warning-icon">⚠️</div>
              <p className="mt-3">
                Are you sure you want to delete <strong>{deptToDelete.name}</strong>?
              </p>
              <p className="text-muted small">
                This action cannot be undone. Department will be permanently removed.
              </p>
              {getDeptUsersCount(deptToDelete.id) > 0 && (
                <Alert variant="warning" className="mt-3 mb-0">
                  ⚠️ This department has {getDeptUsersCount(deptToDelete.id)} members. They will be unassigned.
                </Alert>
              )}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="border-top">
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setDeptToDelete(null);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteDept}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <FiTrash2 className="me-2" />
                Delete Department
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DepartmentManagement;
